import React, { useCallback, useEffect, useRef, useState } from 'react';

// First product photo — lives inside the website itself (the new approved pouch image),
// so it loads immediately without waiting for Firestore. Responsive WebP set.
// Admin panel se upload hui photos uske BAAD slides ki tarah judti hain.
const DEFAULT_IMAGE = {
  src: '/images/product-hero-640.webp',
  thumb: '/images/product-hero-320.webp',
  srcSet: '/images/product-hero-320.webp 320w, /images/product-hero-640.webp 640w, /images/product-hero-900.webp 900w',
};

const PRODUCT_IMG_KEYS = ['product_img_1', 'product_img_2', 'product_img_3', 'product_img_4', 'product_img_5', 'product_img_6', 'product_img_7', 'product_img_8'];

// Shrinks/WebP-converts heavy images already uploaded to Cloudinary at delivery time.
// If Cloudinary disallows the transformation, onError restores the original URL.
const optimize = (url, w) => {
  if (typeof url !== 'string' || !url.includes('/image/upload/')) return url;
  if (/\/upload\/(?:[a-z]{1,3}_[^/]+,?)+\//.test(url)) return url; // already transformed
  return url.replace('/image/upload/', `/image/upload/f_auto,q_auto,w_${w}/`);
};
const fallbackTo = (original) => (e) => {
  const img = e.currentTarget;
  if (img.dataset.fb === '1') return;
  img.dataset.fb = '1';
  img.removeAttribute('srcset');
  img.src = original;
};

// Slider: mobile par ungli se side swipe (native scroll-snap), desktop par arrows +
// mouse drag + thumbnails. Only the first photo loads immediately, the rest lazy — the website stays fast.
export default function ProductGallery({ config, alt }) {
  const admin = PRODUCT_IMG_KEYS.map((k) => config?.[k]).filter(Boolean);
  const images = [DEFAULT_IMAGE.src, ...admin];
  const count = images.length;
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const raf = useRef(0);
  const drag = useRef(null);

  const idx = active < count ? active : 0;

  const goTo = useCallback((i, smooth = true) => {
    const el = trackRef.current;
    if (!el) return;
    const n = (i + count) % count;
    el.scrollTo({ left: n * el.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    setActive(n);
  }, [count]);

  const onScroll = () => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      const el = trackRef.current;
      if (!el || !el.clientWidth) return;
      const i = Math.round(el.scrollLeft / el.clientWidth);
      setActive((p) => (p === i ? p : i));
    });
  };

  useEffect(() => () => { if (raf.current) cancelAnimationFrame(raf.current); }, []);

  // v30: count badalne par zabardasti scroll reset NA karo — Firestore aane par
  // track ki scrollWidth badalti hai; scrollLeft=0 force karne se (aur mandatory
  // with snap) the browser re-snaps, the first image repaints
  // and LCP slips to 7.9s. Proximity snap + natural position = no repaint.

  // Desktop mouse drag (on touch, the browser handles swiping itself)
  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse' || count < 2) return;
    const el = trackRef.current;
    drag.current = { x: e.clientX, left: el.scrollLeft, moved: false };
    el.classList.add('dragging');
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 4) d.moved = true;
    trackRef.current.scrollLeft = d.left - dx;
  };
  const endDrag = (e) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    const el = trackRef.current;
    el.classList.remove('dragging');
    const dx = e.clientX - d.x;
    let n = idx;
    if (Math.abs(dx) > 40) n = idx + (dx < 0 ? 1 : -1);
    n = Math.max(0, Math.min(count - 1, n));
    el.scrollTo({ left: n * el.clientWidth, behavior: 'smooth' });
    setActive(n);
  };

  return (
    <div className="ph-gallery-wrap">
      <div className="ph-main-frame">
        <div
          className="ph-track"
          ref={trackRef}
          onScroll={onScroll}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onPointerCancel={endDrag}
        >
          {images.map((src, i) => {
            const isDef = src === DEFAULT_IMAGE.src;
            // v30: LCP image mobile par <picture> se 640px force (DPR ki wajah se
            // browser 900px pick kar raha tha — 101KB vs 53KB). Desktop par 900px.
            const mainImg = (
              <img
                src={isDef ? src : optimize(src, 900)}
                srcSet={isDef ? undefined : undefined}
                onError={isDef ? undefined : fallbackTo(src)}
                alt={i === 0 ? (alt || 'Organic Slim Fit Tea') : `${alt || 'Organic Slim Fit Tea'} — photo ${i + 1}`}
                className="ph-main-img"
                width="900" height="900"
                fetchPriority={i === 0 ? 'high' : 'low'}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                draggable="false"
              />
            );
            return (
              <div className="ph-slide" key={src + i}>
                {isDef && i === 0 ? (
                  <picture>
                    <source media="(max-width: 800px)" srcSet="/images/product-hero-640.webp" />
                    {mainImg}
                  </picture>
                ) : mainImg}
              </div>
            );
          })}
        </div>
        {count > 1 && (
          <>
            <button type="button" className="ph-arrow ph-arrow-prev" onClick={() => goTo(idx - 1)} aria-label="Previous photo">‹</button>
            <button type="button" className="ph-arrow ph-arrow-next" onClick={() => goTo(idx + 1)} aria-label="Next photo">›</button>
            <span className="ph-counter">{idx + 1} / {count}</span>
          </>
        )}
      </div>
      {count > 1 && (
        <div className="ph-thumb-row">
          {images.map((src, i) => {
            const isDef = src === DEFAULT_IMAGE.src;
            return (
              <button
                key={src + i}
                type="button"
                className={`ph-thumb ${i === idx ? 'active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Product photo ${i + 1}`}
              >
                <img
                  src={isDef ? DEFAULT_IMAGE.thumb : optimize(src, 160)}
                  onError={isDef ? undefined : fallbackTo(src)}
                  alt="" loading="lazy" decoding="async" width="80" height="80"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
